import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useAutoExportSettings } from '../../../hooks/useAutoExportSettings';

/**
 * AutoExportSettingsSection - Toggle controls for auto-export settings on a rule set.
 *
 * Displays two MUI Switch toggles:
 * - AutoExportOnNoDiscrepancy: auto-export when no discrepancies
 * - AutoExportOnDiscrepancy: auto-export with discrepancies
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4
 *
 * @param {string} rulesetId - The ID of the rule set
 */
const AutoExportSettingsSection = ({ rulesetId }) => {
  const {
    autoExportOnNoDiscrepancy,
    autoExportOnDiscrepancy,
    loading,
    saving,
    error,
    success,
    toggleSetting,
    clearError,
    clearSuccess
  } = useAutoExportSettings(rulesetId);

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Auto-Export Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure automatic export behavior for invoices processed under this rule set.
        </Typography>

        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoExportOnNoDiscrepancy}
                  onChange={(e) => toggleSetting('autoExportOnNoDiscrepancy', e.target.checked)}
                  disabled={saving}
                  inputProps={{ 'aria-label': 'Auto-export when no discrepancies' }}
                />
              }
              label="Auto-export when no discrepancies"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
              When enabled, invoices with no discrepancies will be automatically exported without manual review
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoExportOnDiscrepancy}
                  onChange={(e) => toggleSetting('autoExportOnDiscrepancy', e.target.checked)}
                  disabled={saving}
                  inputProps={{ 'aria-label': 'Auto-export with discrepancies' }}
                />
              }
              label="Auto-export with discrepancies"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
              When enabled, invoices with unresolved discrepancies will be automatically exported, bypassing manual review
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={clearSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={clearSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

AutoExportSettingsSection.propTypes = {
  rulesetId: PropTypes.string.isRequired
};

export default AutoExportSettingsSection;
